import React, { useContext, useEffect, useRef, useState } from 'react';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { encode, GlobalId } from '../../../id';
import { AuthContext } from 'api/context';

const PortalRedirect = (props) => {
  const {
    proposalId,
    leadId,
    loading,
    handleSaveProposal = null,
    canOpenPortal = true,
    onBlockedOpen = null,
    onSaveError = null,
  } = props;
  const proposalKey = `${leadId}:${proposalId}`;
  const [preparing, setPreparing] = useState(false);
  const [preparedFor, setPreparedFor] = useState(
    handleSaveProposal ? null : proposalKey
  );
  const preparationRequest = useRef(null);
  const mounted = useRef(true);
  const { company, auth } = useContext(AuthContext);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    setPreparedFor(handleSaveProposal ? null : proposalKey);
    setPreparing(false);
    preparationRequest.current = null;
  }, [handleSaveProposal, proposalKey]);

  const globalCompanyId = encode(
    new GlobalId('Organization', 'legacy', company)
  );
  const globalProposalId = encode(
    new GlobalId('Proposal', 'legacy', proposalId)
  );
  const globalCustomerId = encode(
    new GlobalId('Customer', 'legacy', leadId)
  );
  const session = auth.getToken();

  const homeownerLink = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/apps/customer-portal/${globalCustomerId}?proposalId=${globalProposalId}&organizationId=${globalCompanyId}&viewAs=customer`;
  const consultantRedirect = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/apps/customer-portal/${globalCustomerId}?proposalId=${globalProposalId}&organizationId=${globalCompanyId}`;
  const consultantLink = `${process.env.REACT_APP_PLATFORM_PUBLIC_URL}/auth/signin/legacy?session=${session}&organization=${globalCompanyId}&redirectTo=${encodeURIComponent(
    consultantRedirect
  )}`;

  const preparePortal = () => {
    if (!handleSaveProposal) {
      setPreparedFor(proposalKey);
      return Promise.resolve(true);
    }

    if (preparationRequest.current) return preparationRequest.current;

    setPreparing(true);
    setPreparedFor(null);

    const request = Promise.resolve()
      .then(() => handleSaveProposal())
      .then(() => {
        if (mounted.current && preparationRequest.current === request) {
          setPreparedFor(proposalKey);
        }
        return true;
      })
      .catch((error) => {
        if (mounted.current && preparationRequest.current === request) {
          setPreparedFor(null);
          if (onSaveError) onSaveError(error);
        }
        console.log(error);
        return false;
      })
      .finally(() => {
        if (preparationRequest.current === request) {
          preparationRequest.current = null;
          if (mounted.current) setPreparing(false);
        }
      });

    preparationRequest.current = request;
    return request;
  };

  const handleToggle = (show) => {
    if (!show || loading || preparing) return;

    if (!canOpenPortal) {
      if (onBlockedOpen) onBlockedOpen();
      return;
    }

    preparePortal();
  };

  const handleUnavailableLink = (event) => {
    event.preventDefault();
    if (!canOpenPortal && onBlockedOpen) onBlockedOpen();
  };

  const linksReady =
    canOpenPortal &&
    !loading &&
    !preparing &&
    preparedFor === proposalKey;
  const linkProps = linksReady
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  const accessibilityList = ["WOLFRI", "AERIAL", "AERIA1", "SUNCHE", "JSCONS", "SUNLY2", "TEMPOS", "SIGESO", "BARDIA", "PRODUC"];

  if (!accessibilityList.includes(company) && !company.includes('WOLFR')) {
    return <></>;
  }

  return (
    <DropdownButton
      style={{ display: 'inline' }}
      size={props?.size}
      className={`my-0 py-0 ${
        props?.size === 'sm' ? '.dropdown-menu-sm' : '.dropdown-menu-default'
      }`}
      variant="secondary"
      id="customer-portal-dropdown"
      title={preparing ? 'Preparing...' : 'Customer Portal'}
      disabled={props?.size === 'sm' || loading || preparing}
      onToggle={handleToggle}
    >
      <Dropdown.Item
        className="px-1 my-0"
        disabled={!linksReady}
        href={linksReady ? homeownerLink : undefined}
        onClick={linksReady ? undefined : handleUnavailableLink}
        {...linkProps}
      >
        <small>Homeowner</small>
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item
        className="px-1 my-0"
        disabled={!linksReady}
        href={linksReady ? consultantLink : undefined}
        onClick={linksReady ? undefined : handleUnavailableLink}
        {...linkProps}
      >
        <small>Consultant</small>
      </Dropdown.Item>
    </DropdownButton>
  );
};

PortalRedirect.propTypes = {
  proposalId: PropTypes.string.isRequired,
  leadId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  handleSaveProposal: PropTypes.func,
  canOpenPortal: PropTypes.bool,
  onBlockedOpen: PropTypes.func,
  onSaveError: PropTypes.func,
};

export default PortalRedirect;
